import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Shield, Sun, Moon } from "lucide-react";
import { GlowButton } from "@/components/ui/GlowButton";
import { useAuth } from "@/contexts/AuthContext";
import { PersonaSwitcher } from "./PersonaSwitcher";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're in the CEO/Founder context for orange theming
  const isFounderContext = location.pathname.startsWith('/for-founders') || 
                           location.pathname.startsWith('/ceo/sample-report');

  const SIGN_IN_URL = "https://app.provenance365.com";

  useEffect(() => {
    // Check initial theme
    const isDarkMode = document.documentElement.classList.contains("dark");
    setIsDark(isDarkMode);
  }, []);

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    if (newIsDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  };

  const navItems = isFounderContext
    ? [
        { label: "How it works", href: "/for-founders#how-it-works" },
        { label: "Pricing", href: "/for-founders#pricing" },
        { label: "About", href: "/for-founders/about" },
        { label: "Contact", href: "/for-founders/contact" },
      ]
    : [
        { label: "Our approach", href: "/#how-it-works" },
        { label: "About", href: "/about" },
        { label: "Contact", href: "/contact" },
      ];

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl"
    >
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        {/* Logo - Orange in Founder context, cream elsewhere */}
        <Link to="/" className="flex items-center gap-2">
          <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${
            isFounderContext 
              ? 'bg-gradient-to-br from-amber-500 to-orange-500' 
              : 'bg-cream'
          }`}>
            <Shield className={`h-5 w-5 ${isFounderContext ? 'text-white' : 'text-background'}`} />
          </div>
          <span className="font-display text-xl font-bold tracking-tight">
            PROVENANCE
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <PersonaSwitcher />
          {navItems.map((item) => {
            const hasHash = item.href.includes("#");
            if (hasHash) {
              const [path, hash] = item.href.split("#");
              const isOnPage = window.location.pathname === path || window.location.pathname === path.replace(/\/$/, "");
              return (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => {
                    if (isOnPage) {
                      e.preventDefault();
                      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                  className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  {item.label}
                </a>
              );
            }
            return (
              <Link
                key={item.label}
                to={item.href}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {item.label}
              </Link>
            );
          })}
        </div>

        {/* CTA Buttons */}
        <div className="hidden md:flex items-center gap-3">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            aria-label="Toggle theme"
          >
            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          
          {user ? (
            <>
              <GlowButton variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                Dashboard
              </GlowButton>
              <GlowButton variant="secondary" size="sm" onClick={handleSignOut}>
                Sign out
              </GlowButton>
            </>
          ) : (
            <>
              <GlowButton variant="ghost" size="sm" onClick={() => { window.location.href = SIGN_IN_URL; }}>
                Sign in
              </GlowButton>
              <GlowButton 
                variant="primary" 
                size="sm" 
                onClick={() => navigate("/demo")}
                className={isFounderContext ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' : ''}
              >
                Try demo
              </GlowButton>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            aria-label="Toggle theme"
          >
            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 text-foreground"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden border-t border-border bg-background px-4 py-6"
        >
          <div className="flex flex-col gap-4">
            <PersonaSwitcher onNavigate={() => setIsOpen(false)} className="self-start" />
            {navItems.map((item) => {
              const hasHash = item.href.includes("#");
              if (hasHash) {
                const [path, hash] = item.href.split("#");
                const isOnPage = window.location.pathname === path || window.location.pathname === path.replace(/\/$/, "");
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    className="text-sm font-medium text-muted-foreground"
                    onClick={(e) => {
                      setIsOpen(false);
                      if (isOnPage) {
                        e.preventDefault();
                        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                  >
                    {item.label}
                  </a>
                );
              }
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className="text-sm font-medium text-muted-foreground"
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </Link>
              );
            })}
            <div className="flex flex-col gap-2 pt-4 border-t border-border">
              {user ? (
                <>
                  <GlowButton variant="ghost" size="sm" onClick={() => { navigate("/dashboard"); setIsOpen(false); }}>
                    Dashboard
                  </GlowButton>
                  <GlowButton variant="secondary" size="sm" onClick={() => { handleSignOut(); setIsOpen(false); }}>
                    Sign out
                  </GlowButton>
                </>
              ) : (
                <>
                  <GlowButton variant="ghost" size="sm" onClick={() => { window.location.href = SIGN_IN_URL; }}>
                    Sign in
                  </GlowButton>
                  <GlowButton 
                    variant="primary" 
                    size="sm" 
                    onClick={() => { navigate("/demo"); setIsOpen(false); }}
                    className={isFounderContext ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' : ''}
                  >
                    Try demo
                  </GlowButton>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};
